// ─── SLAContractsView — gestão de SLAs (Petrotec Angola) ──────────────────
//
// Tabela de SLAs por (cliente final × tipo de intervenção). Diferente dos
// drilldowns anteriores: é uma vista AGREGADA, acedida directamente do
// menu (não vem de um drilldown). Mostra todos os contratos em vigor e
// permite filtrar por cliente final e tipo.
//
// Tipo de intervenção × horas-alvo típicas (Angola):
//   · accident   → ~2h    (emergência num posto que não pode parar)
//   · corrective → ~24h   (avaria normal)
//   · preventive → ~168h  (manutenção, 7 dias)
// Estes são SUGESTÕES no form, não defaults — o operador define caso a caso.

import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useFormCardScroll } from '../../utils/manageHelpers';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip, Switch,
  FormControlLabel, CircularProgress, Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import TimerIcon from '@material-ui/icons/Timer';
import BuildIcon from '@material-ui/icons/Build';
import WarningIcon from '@material-ui/icons/Warning';
import EventIcon from '@material-ui/icons/Event';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import { useDispatch } from 'react-redux';
import { setActiveView } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';

// Alinhado com o BM oficial Petrotec (TIPO DE INTERVENÇÃO):
// EVENTUAL, PREVENTIVA, P/ACIDENTE, P/DESGASTE.
// `corrective` (criado antes da análise do BM) mantém-se como alias do
// `eventual` para retrocompatibilidade — SLAs antigos continuam a
// funcionar e o operador pode editá-los para o novo termo.
const INTERVENTION_TYPES = [
  { key: 'accident',   label: { pt: 'P/Acidente',  en: 'Accident' },    color: '#dc2626', defaultHours: 2,   Icon: WarningIcon },
  { key: 'eventual',   label: { pt: 'Eventual',    en: 'Eventual' },    color: '#f59e0b', defaultHours: 24,  Icon: BuildIcon },
  { key: 'preventive', label: { pt: 'Preventiva',  en: 'Preventive' },  color: '#0ea5e9', defaultHours: 168, Icon: EventIcon },
  { key: 'wear',       label: { pt: 'P/Desgaste',  en: 'Wear' },        color: '#a855f7', defaultHours: 336, Icon: FlashOnIcon },
  // Legacy — mantido para SLAs antigos. Pode ser tratado como alias do "eventual".
  { key: 'corrective', label: { pt: 'Correctiva (legado)', en: 'Corrective (legacy)' }, color: '#94a3b8', defaultHours: 24, Icon: BuildIcon, legacy: true },
];
const TYPE_META = INTERVENTION_TYPES.reduce((acc, t) => { acc[t.key] = t; return acc; }, {});

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
    background: '#f8fafc', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
  },
  titleBox: { flex: 1, minWidth: 0 },
  title: {
    fontSize: '1.05rem', fontWeight: 800, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  subtitle: { fontSize: '0.7rem', color: '#64748b', marginTop: 2 },
  toolbar: {
    display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center',
    borderBottom: '1px solid #f1f5f9', background: '#fff', flexWrap: 'wrap',
  },
  filterSelect: { minWidth: 200 },
  addButton: {
    background: '#3b82f6',
    color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 700,
    padding: '6px 16px', fontSize: '0.74rem', boxShadow: 'none',
    '&:hover': { background: '#2563eb', boxShadow: '0 2px 6px rgba(59,130,246,0.25)' },
  },
  saveButton: {
    background: '#3b82f6', color: '#fff',
    textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
    padding: '6px 18px', boxShadow: 'none',
    '&:hover': { background: '#2563eb' },
  },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  card: {
    background: '#fff', borderRadius: 12, padding: '14px 18px', marginBottom: 10,
    border: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', gap: 14,
    transition: 'all 0.2s',
    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' },
  },
  cardInactive: { opacity: 0.55, background: '#f8fafc' },
  cardIcon: {
    width: 42, height: 42, borderRadius: 10, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  cardMain: { flex: 1, minWidth: 0 },
  cardName: { fontSize: '0.92rem', fontWeight: 700, color: '#0f172a' },
  cardMeta: {
    fontSize: '0.7rem', color: '#64748b', marginTop: 4,
    display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
  },
  hoursPill: {
    display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
    borderRadius: 999, fontWeight: 800, fontSize: '0.82rem',
  },
  cardActions: { display: 'flex', gap: 4, alignItems: 'center' },
  empty: {
    padding: '80px 24px', textAlign: 'center', color: '#94a3b8',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  formCard: {
    background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  formTitle: {
    fontSize: '0.86rem', fontWeight: 700, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  formRow2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  formRow3: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
}));

const EMPTY_FORM = {
  finalCustomerId: '',
  interventionType: 'corrective',
  tier: '',
  targetHours: 24,
  targetResolutionHours: '',
  targetKm: '',
  validFrom: '',
  validTo: '',
  notes: '',
};

// Tiers comerciais. As cores são usadas no chip e devem reflectir a
// urgência percepcionada pelo operador (vermelho = alarme imediato).
const TIER_META = {
  emergent:  { labelPt: 'Emergente', labelEn: 'Emergent',  color: '#dc2626', bg: '#fee2e2' },
  urgent:    { labelPt: 'Urgente',   labelEn: 'Urgent',    color: '#ea580c', bg: '#ffedd5' },
  normal:    { labelPt: 'Normal',    labelEn: 'Normal',    color: '#0ea5e9', bg: '#e0f2fe' },
  scheduled: { labelPt: 'Programada', labelEn: 'Scheduled', color: '#16a34a', bg: '#dcfce7' },
};
const TIER_KEYS = ['emergent', 'urgent', 'normal', 'scheduled'];

function formatHoursLabel(h, pt) {
  if (h < 24) return `${h}h`;
  if (h % 24 === 0) return `${h / 24}${pt ? 'd' : 'd'}`;
  return `${h}h`;
}

// Calcula estado do SLA pelo seu período de validade
function computeStatus(s, nowMs) {
  if (!s.isActive) return 'inactive';
  const from = new Date(s.validFrom).getTime();
  const to = s.validTo ? new Date(s.validTo).getTime() : null;
  if (nowMs < from) return 'future';
  if (to && nowMs > to) return 'expired';
  return 'active';
}

function SLAContractsView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]); // para o select do form e filtro
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterFC, setFilterFC] = useState('');       // '' = todos
  const [filterType, setFilterType] = useState('');   // '' = todos
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const formCardRef = useFormCardScroll(editingId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [seedConfirm, setSeedConfirm] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null); // { created, skipped, customers }

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const nowMs = Date.now();

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterFC) params.set('finalCustomerId', String(filterFC));
      if (filterType) params.set('interventionType', filterType);
      if (showInactive) params.set('includeInactive', '1');
      const [slaR, fcR] = await Promise.all([
        fetch(`/planner-api/sla-contracts?${params.toString()}`, { headers }),
        fetch(`/planner-api/final-customers?includeInactive=1`, { headers }),
      ]);
      if (!slaR.ok) throw new Error(`HTTP ${slaR.status}`);
      if (!fcR.ok) throw new Error(`HTTP ${fcR.status}`);
      setItems(await slaR.json());
      setCustomers(await fcR.json());
    } catch (e) {
      console.error('SLAContracts fetch error:', e);
      setError(pt ? 'Não foi possível carregar a lista.' : 'Could not load list.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filterFC, filterType, showInactive, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const activeCustomers = useMemo(() => customers.filter(c => c.isActive), [customers]);

  function startCreate() {
    setForm({
      ...EMPTY_FORM,
      finalCustomerId: activeCustomers[0]?.id || '',
      validFrom: new Date(nowMs).toISOString().slice(0, 10),
    });
    setEditingId('new');
  }

  function startEdit(s) {
    setForm({
      finalCustomerId: s.finalCustomerId,
      interventionType: s.interventionType,
      tier: s.tier || '',
      targetHours: s.targetHours,
      targetResolutionHours: s.targetResolutionHours != null ? s.targetResolutionHours : '',
      targetKm: s.targetKm != null ? s.targetKm : '',
      validFrom: s.validFrom ? String(s.validFrom).slice(0, 10) : '',
      validTo: s.validTo ? String(s.validTo).slice(0, 10) : '',
      notes: s.notes || '',
    });
    setEditingId(s.id);
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  // Atualiza horas-alvo automaticamente quando muda o tipo na criação
  function onTypeChange(type) {
    const meta = TYPE_META[type];
    setForm(f => ({
      ...f,
      interventionType: type,
      targetHours: editingId === 'new' ? (meta?.defaultHours || f.targetHours) : f.targetHours,
    }));
  }

  async function saveForm() {
    if (!form.finalCustomerId) {
      setError(pt ? 'Cliente final obrigatório.' : 'Final customer required.');
      return;
    }
    if (!form.targetHours || form.targetHours < 1) {
      setError(pt ? 'Horas-alvo inválidas.' : 'Invalid target hours.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const isNew = editingId === 'new';
      let url, method, body;
      if (isNew) {
        url = '/planner-api/sla-contracts';
        method = 'POST';
        body = JSON.stringify({
          finalCustomerId: parseInt(form.finalCustomerId),
          interventionType: form.interventionType,
          tier: form.tier || null,
          targetHours: parseInt(form.targetHours),
          targetResolutionHours: form.targetResolutionHours !== '' ? parseInt(form.targetResolutionHours) : null,
          targetKm: form.targetKm !== '' ? parseInt(form.targetKm) : null,
          validFrom: form.validFrom || null,
          validTo: form.validTo || null,
          notes: form.notes.trim() || null,
        });
      } else {
        url = `/planner-api/sla-contracts/${editingId}`;
        method = 'PUT';
        body = JSON.stringify({
          tier: form.tier || null,
          targetHours: parseInt(form.targetHours),
          targetResolutionHours: form.targetResolutionHours !== '' ? parseInt(form.targetResolutionHours) : null,
          targetKm: form.targetKm !== '' ? parseInt(form.targetKm) : null,
          validTo: form.validTo || null,
          notes: form.notes.trim() || null,
        });
      }
      const r = await fetch(url, { method, headers, body });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchAll();
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao guardar.' : 'Save error.'));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(s) {
    try {
      const r = await fetch(`/planner-api/sla-contracts/${s.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ isActive: !s.isActive }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAll();
    } catch (e) {
      setError(e.message);
    }
  }

  async function confirmDeleteNow() {
    if (!deleteConfirm) return;
    try {
      const r = await fetch(`/planner-api/sla-contracts/${deleteConfirm.id}`, {
        method: 'DELETE', headers,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDeleteConfirm(null);
      await fetchAll();
    } catch (e) {
      setError(e.message);
      setDeleteConfirm(null);
    }
  }

  async function seedDefaults() {
    setSeeding(true);
    setSeedResult(null);
    setError(null);
    try {
      const r = await fetch('/planner-api/sla-contracts/defaults', {
        method: 'POST', headers, body: JSON.stringify({}),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setSeedResult(data);
      await fetchAll();
    } catch (e) {
      setError(e.message || (pt ? 'Erro a criar SLAs padrão.' : 'Error creating default SLAs.'));
    } finally {
      setSeeding(false);
      setSeedConfirm(false);
    }
  }

  const STATUS_META = {
    active:   { label: { pt: 'Em vigor', en: 'Active' }, color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    future:   { label: { pt: 'Futuro',   en: 'Future' }, color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
    expired:  { label: { pt: 'Caducado', en: 'Expired' }, color: '#94a3b8', bg: '#f1f5f9' },
    inactive: { label: { pt: 'Inactivo', en: 'Inactive' }, color: '#94a3b8', bg: '#f1f5f9' },
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back to Maintenance'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Typography className={classes.title}>
            <TimerIcon style={{ fontSize: 20, color: '#22c55e' }} />
            {pt ? 'Contratos SLA' : 'SLA Contracts'}
          </Typography>
          <Typography className={classes.subtitle}>
            {pt
              ? 'Horas-alvo por cliente final × tipo de intervenção'
              : 'Target hours per customer × intervention type'}
          </Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}>
            <RefreshIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <FormControl size="small" variant="outlined" className={classes.filterSelect}>
          <InputLabel>{pt ? 'Cliente final' : 'Final customer'}</InputLabel>
          <Select
            label={pt ? 'Cliente final' : 'Final customer'}
            value={filterFC}
            onChange={e => setFilterFC(e.target.value)}
          >
            <MenuItem value="">{pt ? 'Todos' : 'All'}</MenuItem>
            {activeCustomers.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" variant="outlined" style={{ minWidth: 160 }}>
          <InputLabel>{pt ? 'Tipo' : 'Type'}</InputLabel>
          <Select
            label={pt ? 'Tipo' : 'Type'}
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
          >
            <MenuItem value="">{pt ? 'Todos' : 'All'}</MenuItem>
            {INTERVENTION_TYPES.map(t => (
              <MenuItem key={t.key} value={t.key}>
                {t.label[pt ? 'pt' : 'en']}{t.legacy ? ' ⚠' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
            />
          }
          label={
            <Typography style={{ fontSize: '0.75rem', color: '#475569' }}>
              {pt ? 'Mostrar inactivos' : 'Show inactive'}
            </Typography>
          }
        />
        <Box style={{ flex: 1 }} />
        <Tooltip title={pt
          ? 'Cria SLA padrão (Acidente 2h, Correctiva 24h, Preventiva 168h) para todos os clientes finais activos que ainda não têm SLA.'
          : 'Creates default SLAs (Accident 2h, Corrective 24h, Preventive 168h) for active customers without one.'}>
          <span>
            <Button
              onClick={() => setSeedConfirm(true)}
              disabled={editingId !== null || activeCustomers.length === 0 || seeding}
              startIcon={seeding
                ? <CircularProgress size={14} style={{ color: '#475569' }} />
                : <FlashOnIcon style={{ fontSize: 16 }} />}
              style={{
                textTransform: 'none', fontSize: '0.74rem', fontWeight: 700,
                color: '#475569', background: '#fff',
                border: '1px solid #e2e8f0', borderRadius: 8,
                padding: '5px 14px',
              }}
            >
              {pt ? 'SLAs padrão' : 'Default SLAs'}
            </Button>
          </span>
        </Tooltip>
        <Button
          className={classes.addButton}
          startIcon={<AddIcon style={{ fontSize: 18 }} />}
          onClick={startCreate}
          disabled={editingId !== null || activeCustomers.length === 0}
        >
          {pt ? 'Novo SLA' : 'New SLA'}
        </Button>
      </Box>

      <Box className={classes.list}>
        {error && (
          <Box style={{
            padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', color: '#991b1b',
            borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
          }}>
            {error}
          </Box>
        )}

        {seedResult && (
          <Box style={{
            padding: '10px 14px', background: '#f0fdf4',
            border: '1px solid #bbf7d0', color: '#166534',
            borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          }}>
            <span>
              {pt
                ? `Criados ${seedResult.created} SLAs padrão para ${seedResult.customers} cliente(s). ${seedResult.skipped > 0 ? `${seedResult.skipped} já existiam.` : ''}`
                : `Created ${seedResult.created} default SLAs for ${seedResult.customers} customer(s). ${seedResult.skipped > 0 ? `${seedResult.skipped} already existed.` : ''}`}
            </span>
            <IconButton size="small" onClick={() => setSeedResult(null)}>
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}

        {activeCustomers.length === 0 && !loading && (
          <Box style={{
            padding: '10px 14px', background: '#fffbeb',
            border: '1px solid #fde68a', color: '#92400e',
            borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
          }}>
            {pt
              ? 'Sem clientes finais activos. Cria primeiro um cliente final em "Clientes Finais (Angola)" para depois poderes definir SLAs.'
              : 'No active final customers. Create one in "Final Customers (Angola)" first.'}
          </Box>
        )}

        {editingId !== null && (
          <Box ref={formCardRef} className={classes.formCard}>
            <Typography className={classes.formTitle}>
              <TimerIcon style={{ fontSize: 18, color: '#22c55e' }} />
              {editingId === 'new'
                ? (pt ? 'Novo SLA' : 'New SLA')
                : (pt ? 'Editar SLA' : 'Edit SLA')}
            </Typography>
            <Box className={classes.formRow3}>
              <FormControl size="small" variant="outlined" disabled={editingId !== 'new'}>
                <InputLabel>{pt ? 'Cliente final *' : 'Final customer *'}</InputLabel>
                <Select
                  label={pt ? 'Cliente final *' : 'Final customer *'}
                  value={form.finalCustomerId}
                  onChange={e => setForm({ ...form, finalCustomerId: e.target.value })}
                >
                  {activeCustomers.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" variant="outlined" disabled={editingId !== 'new'}>
                <InputLabel>{pt ? 'Tipo *' : 'Type *'}</InputLabel>
                <Select
                  label={pt ? 'Tipo *' : 'Type *'}
                  value={form.interventionType}
                  onChange={e => onTypeChange(e.target.value)}
                >
                  {/* No FORM de criação, esconder legacy. Em modo edit, se
                      o valor actual é legacy, mostrar para preservar. */}
                  {INTERVENTION_TYPES
                    .filter(t => !t.legacy || form.interventionType === t.key)
                    .map(t => (
                      <MenuItem key={t.key} value={t.key}>
                        {t.label[pt ? 'pt' : 'en']} ({t.defaultHours}h)
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TextField
                label={pt ? 'Resposta — horas-alvo *' : 'Response — target hours *'}
                size="small" variant="outlined" type="number"
                value={form.targetHours}
                onChange={e => setForm({ ...form, targetHours: e.target.value })}
                inputProps={{ min: 1, max: 8760 }}
                helperText={pt ? `Tempo até chegar (createdAt → arrivedAt)` : `Time to arrive`}
              />
            </Box>
            <Box className={classes.formRow2}>
              <TextField
                label={pt ? 'Resolução — horas-alvo (opcional)' : 'Resolution — target hours (optional)'}
                size="small" variant="outlined" type="number"
                value={form.targetResolutionHours}
                onChange={e => setForm({ ...form, targetResolutionHours: e.target.value })}
                inputProps={{ min: 1, max: 8760 }}
                helperText={pt
                  ? 'Tempo total até concluir (createdAt → completedAt). Vazio = sem SLA Resolução.'
                  : 'Total time until completion. Empty = no Resolution SLA.'}
              />
              <FormControl size="small" variant="outlined">
                <InputLabel>{pt ? 'Nível (tier)' : 'Tier'}</InputLabel>
                <Select
                  label={pt ? 'Nível (tier)' : 'Tier'}
                  value={form.tier}
                  onChange={e => setForm({ ...form, tier: e.target.value })}
                >
                  <MenuItem value=""><em>{pt ? '— Sem nível —' : '— No tier —'}</em></MenuItem>
                  {TIER_KEYS.map(k => (
                    <MenuItem key={k} value={k}>
                      {pt ? TIER_META[k].labelPt : TIER_META[k].labelEn}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={pt ? 'Raio km (opcional)' : 'Radius km (optional)'}
                size="small" variant="outlined" type="number"
                value={form.targetKm}
                onChange={e => setForm({ ...form, targetKm: e.target.value })}
                inputProps={{ min: 0, max: 100000 }}
                helperText={pt ? 'Ex.: 50km, 100km' : 'e.g. 50km, 100km'}
              />
            </Box>
            <Box className={classes.formRow2}>
              <TextField
                label={pt ? 'Válido desde *' : 'Valid from *'}
                size="small" variant="outlined" type="date"
                value={form.validFrom}
                onChange={e => setForm({ ...form, validFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={editingId !== 'new'}
              />
              <TextField
                label={pt ? 'Válido até (opcional)' : 'Valid to (optional)'}
                size="small" variant="outlined" type="date"
                value={form.validTo}
                onChange={e => setForm({ ...form, validTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText={pt ? 'Sem data = vigora indefinidamente' : 'Empty = open-ended'}
              />
            </Box>
            <TextField
              label={pt ? 'Notas' : 'Notes'}
              size="small" variant="outlined"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              fullWidth multiline minRows={2}
            />
            <Box className={classes.formActions}>
              <Button
                onClick={cancelEdit}
                style={{ textTransform: 'none', fontSize: '0.78rem' }}
                disabled={saving}
              >
                {pt ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                onClick={saveForm}
                disabled={saving}
                className={classes.saveButton}
                startIcon={saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <CheckIcon style={{ fontSize: 16 }} />}
              >
                {pt ? 'Guardar' : 'Save'}
              </Button>
            </Box>
            {editingId !== 'new' && (
              <Typography style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>
                {pt
                  ? 'Para alterar cliente/tipo/data de início, cria um SLA novo (o histórico fica preservado).'
                  : 'To change customer/type/start date, create a new SLA (history is preserved).'}
              </Typography>
            )}
          </Box>
        )}

        {loading && items.length === 0 ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} style={{ color: '#3b82f6' }} />
            <Typography style={{ fontSize: '0.8rem' }}>
              {pt ? 'A carregar…' : 'Loading…'}
            </Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box className={classes.empty}>
            <TimerIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
            <Typography style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {filterFC || filterType
                ? (pt ? 'Sem SLAs para os filtros aplicados.' : 'No SLAs for current filters.')
                : (pt ? 'Ainda não há SLAs definidos.' : 'No SLAs defined yet.')}
            </Typography>
            {!filterFC && !filterType && activeCustomers.length > 0 && (
              <Button
                className={classes.addButton}
                startIcon={<AddIcon style={{ fontSize: 18 }} />}
                onClick={startCreate}
                disabled={editingId !== null}
              >
                {pt ? 'Criar primeiro' : 'Create first one'}
              </Button>
            )}
          </Box>
        ) : (
          items.map(s => {
            const tMeta = TYPE_META[s.interventionType] || TYPE_META.corrective;
            const TIcon = tMeta.Icon;
            const status = computeStatus(s, nowMs);
            const sMeta = STATUS_META[status];
            return (
              <Box
                key={s.id}
                className={`${classes.card} ${!s.isActive ? classes.cardInactive : ''}`}
              >
                <Box className={classes.cardIcon} style={{ background: `${tMeta.color}1A` }}>
                  <TIcon style={{ fontSize: 22, color: tMeta.color }} />
                </Box>
                <Box className={classes.cardMain}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Typography className={classes.cardName}>{s.finalCustomerName}</Typography>
                    <Chip
                      label={tMeta.label[pt ? 'pt' : 'en']}
                      size="small"
                      style={{
                        height: 18, fontSize: '0.6rem', fontWeight: 700,
                        background: `${tMeta.color}1A`, color: tMeta.color,
                      }}
                    />
                    {s.tier && TIER_META[s.tier] && (
                      <Chip
                        label={pt ? TIER_META[s.tier].labelPt : TIER_META[s.tier].labelEn}
                        size="small"
                        style={{
                          height: 18, fontSize: '0.6rem', fontWeight: 700,
                          background: TIER_META[s.tier].bg, color: TIER_META[s.tier].color,
                        }}
                      />
                    )}
                    <Chip
                      label={sMeta.label[pt ? 'pt' : 'en']}
                      size="small"
                      style={{
                        height: 18, fontSize: '0.6rem', fontWeight: 700,
                        background: sMeta.bg, color: sMeta.color,
                      }}
                    />
                  </Box>
                  <Box className={classes.cardMeta}>
                    <span>
                      {pt ? 'desde' : 'from'} {String(s.validFrom).slice(0, 10)}
                      {s.validTo ? ` ${pt ? 'até' : 'to'} ${String(s.validTo).slice(0, 10)}` : ''}
                    </span>
                    {s.notes && (
                      <span style={{ fontStyle: 'italic', color: '#475569' }}>
                        “{s.notes}”
                      </span>
                    )}
                  </Box>
                </Box>
                <Box className={classes.hoursPill} style={{ background: `${tMeta.color}14`, color: tMeta.color }}>
                  <TimerIcon style={{ fontSize: 14 }} />
                  <span title={pt ? 'Resposta — tempo até chegar' : 'Response — time to arrive'}>
                    {formatHoursLabel(s.targetHours, pt)}
                  </span>
                  {s.targetResolutionHours != null && (
                    <span style={{ marginLeft: 6, fontWeight: 600, opacity: 0.85 }}
                      title={pt ? 'Resolução — tempo total até concluir' : 'Resolution — total time to completion'}>
                      / {formatHoursLabel(s.targetResolutionHours, pt)}
                    </span>
                  )}
                  {s.targetKm != null && (
                    <span style={{ marginLeft: 6, fontWeight: 600, opacity: 0.85 }}>
                      · {s.targetKm} km
                    </span>
                  )}
                </Box>
                <Box className={classes.cardActions}>
                  <Tooltip title={s.isActive ? (pt ? 'Desactivar' : 'Deactivate') : (pt ? 'Reactivar' : 'Reactivate')}>
                    <IconButton size="small" onClick={() => toggleActive(s)}>
                      {s.isActive
                        ? <CloseIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                        : <CheckIcon style={{ fontSize: 18, color: '#22c55e' }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Editar' : 'Edit'}>
                    <IconButton size="small" onClick={() => startEdit(s)} disabled={editingId !== null}>
                      <EditIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Eliminar (soft)' : 'Delete (soft)'}>
                    <IconButton size="small" onClick={() => setDeleteConfirm({ id: s.id, label: `${s.finalCustomerName} · ${tMeta.label[pt ? 'pt' : 'en']}` })}>
                      <DeleteIcon style={{ fontSize: 16, color: '#ef4444' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <ConfirmDialog
        open={seedConfirm}
        title={pt ? 'Criar SLAs padrão?' : 'Create default SLAs?'}
        message={pt
          ? `Vão ser criados 3 SLAs (Acidente 2h, Correctiva 24h, Preventiva 168h) para cada um dos ${activeCustomers.length} cliente(s) final(is) activo(s) que ainda não tenham SLA do mesmo tipo. SLAs existentes não são alterados.`
          : `3 SLAs (Accident 2h, Corrective 24h, Preventive 168h) will be created for each of the ${activeCustomers.length} active final customer(s) that don't already have one. Existing SLAs are not changed.`}
        severity="info"
        confirmLabel={pt ? 'Criar' : 'Create'}
        cancelLabel={pt ? 'Cancelar' : 'Cancel'}
        onConfirm={seedDefaults}
        onClose={() => setSeedConfirm(false)}
      />

      <ConfirmDialog
        open={!!deleteConfirm}
        title={pt ? 'Desactivar SLA?' : 'Deactivate SLA?'}
        message={deleteConfirm
          ? (pt
            ? `"${deleteConfirm.label}" passa a inactivo. O histórico mantém-se. Pode ser reactivado em qualquer momento.`
            : `"${deleteConfirm.label}" will be deactivated. History is preserved. Can be reactivated at any time.`)
          : ''}
        severity="warning"
        confirmLabel={pt ? 'Desactivar' : 'Deactivate'}
        cancelLabel={pt ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDeleteNow}
        onClose={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}

export default SLAContractsView;
